'use client';

import { useState, useMemo } from 'react';
import Icon from '@/components/ui/AppIcon';

type EventCategory = 'admin' | 'user' | 'booking' | 'system' | 'security';
type Severity = 'info' | 'warning' | 'error' | 'critical';

interface AuditLog {
  id: string;
  timestamp: Date;
  category: EventCategory;
  action: string;
  actor: string;
  actorType: 'admin' | 'user' | 'system';
  target: string;
  details: string;
  severity: Severity;
  ipAddress: string;
  sessionId: string;
  status: 'success' | 'failed' | 'pending';
  metadata?: Record<string, string>;
}

const CATEGORY_CONFIG: Record<EventCategory, { label: string; icon: string; color: string; bg: string }> = {
  admin: { label: 'Admin', icon: 'Cog6ToothIcon', color: 'text-purple-600', bg: 'bg-purple-100' },
  user: { label: 'User', icon: 'UserCircleIcon', color: 'text-blue-600', bg: 'bg-blue-100' },
  booking: { label: 'Booking', icon: 'TicketIcon', color: 'text-green-600', bg: 'bg-green-100' },
  system: { label: 'System', icon: 'ServerIcon', color: 'text-gray-600', bg: 'bg-gray-100' },
  security: { label: 'Security', icon: 'ShieldExclamationIcon', color: 'text-red-600', bg: 'bg-red-100' },
};

const SEVERITY_CONFIG: Record<Severity, { label: string; color: string; bg: string; dot: string }> = {
  info: { label: 'Info', color: 'text-blue-600', bg: 'bg-blue-50', dot: 'bg-blue-400' },
  warning: { label: 'Warning', color: 'text-yellow-700', bg: 'bg-yellow-50', dot: 'bg-yellow-500' },
  error: { label: 'Error', color: 'text-red-600', bg: 'bg-red-50', dot: 'bg-red-500' },
  critical: { label: 'Critical', color: 'text-red-800', bg: 'bg-red-100', dot: 'bg-red-700' },
};

const SAMPLE_LOGS: AuditLog[] = [
  { id: 'LOG-001', timestamp: new Date(Date.now() - 2 * 60000), category: 'admin', action: 'CONFIG_UPDATE', actor: 'admin@dialforflight.co.uk', actorType: 'admin', target: 'AI Configuration', details: 'Updated Anthropic model from claude-3-opus to claude-sonnet-4-5. Max tokens changed from 1024 to 2048.', severity: 'info', ipAddress: '192.168.1.10', sessionId: 'sess_a1b2c3', status: 'success', metadata: { model: 'claude-sonnet-4-5', tokens: '2048' } },
  { id: 'LOG-002', timestamp: new Date(Date.now() - 8 * 60000), category: 'booking', action: 'BOOKING_CREATED', actor: 'sarah.johnson@email.com', actorType: 'user', target: 'Booking AB3K7X', details: 'New booking created for LHR→DXB on 15 Mar 2026. Amount: £842. Payment method: Visa ending 4242.', severity: 'info', ipAddress: '85.234.12.45', sessionId: 'sess_d4e5f6', status: 'success', metadata: { pnr: 'AB3K7X', amount: '£842' } },
  { id: 'LOG-003', timestamp: new Date(Date.now() - 15 * 60000), category: 'security', action: 'LOGIN_FAILED', actor: 'unknown@hacker.com', actorType: 'user', target: 'Admin Panel', details: 'Failed login attempt to admin panel. Invalid credentials. IP flagged for suspicious activity.', severity: 'critical', ipAddress: '203.45.67.89', sessionId: 'sess_x9y8z7', status: 'failed', metadata: { attempts: '3', flagged: 'yes' } },
  { id: 'LOG-004', timestamp: new Date(Date.now() - 22 * 60000), category: 'user', action: 'PROFILE_UPDATE', actor: 'james.wilson@email.com', actorType: 'user', target: 'User Profile #U-4821', details: 'User updated contact details. Phone number and email address changed.', severity: 'info', ipAddress: '78.123.45.67', sessionId: 'sess_g7h8i9', status: 'success', metadata: { fields: 'phone, email' } },
  { id: 'LOG-005', timestamp: new Date(Date.now() - 35 * 60000), category: 'system', action: 'API_RATE_LIMIT', actor: 'system', actorType: 'system', target: 'Anthropic API', details: 'API rate limit warning triggered. Usage at 85% of monthly quota. Auto-throttling enabled.', severity: 'warning', ipAddress: '10.0.0.1', sessionId: 'sys_auto', status: 'success', metadata: { usage: '85%', throttle: 'enabled' } },
  { id: 'LOG-006', timestamp: new Date(Date.now() - 48 * 60000), category: 'admin', action: 'USER_BANNED', actor: 'admin@dialforflight.co.uk', actorType: 'admin', target: 'User #U-9934', details: 'User account suspended due to fraudulent booking attempts. 5 chargebacks detected in 24 hours.', severity: 'warning', ipAddress: '192.168.1.10', sessionId: 'sess_a1b2c3', status: 'success', metadata: { reason: 'fraud', chargebacks: '5' } },
  { id: 'LOG-007', timestamp: new Date(Date.now() - 1.2 * 3600000), category: 'booking', action: 'PAYMENT_FAILED', actor: 'emma.davis@email.com', actorType: 'user', target: 'Booking Attempt #BA-7821', details: 'Payment processing failed for LHR→CDG booking. Card declined by issuing bank. Customer notified.', severity: 'error', ipAddress: '91.234.56.78', sessionId: 'sess_j1k2l3', status: 'failed', metadata: { reason: 'card_declined', amount: '£234' } },
  { id: 'LOG-008', timestamp: new Date(Date.now() - 2 * 3600000), category: 'system', action: 'BACKUP_COMPLETED', actor: 'system', actorType: 'system', target: 'Database Backup', details: 'Scheduled daily backup completed successfully. 2.4GB compressed. Stored in 3 geographic regions.', severity: 'info', ipAddress: '10.0.0.1', sessionId: 'sys_backup', status: 'success', metadata: { size: '2.4GB', regions: '3' } },
  { id: 'LOG-009', timestamp: new Date(Date.now() - 3 * 3600000), category: 'admin', action: 'CONTENT_PUBLISHED', actor: 'content@dialforflight.co.uk', actorType: 'admin', target: 'Blog Article #ART-234', details: 'New blog article published: "Top 10 Budget Destinations for 2026". SEO optimized with 12 keywords.', severity: 'info', ipAddress: '192.168.1.15', sessionId: 'sess_m4n5o6', status: 'success', metadata: { article: '#ART-234', keywords: '12' } },
  { id: 'LOG-010', timestamp: new Date(Date.now() - 4 * 3600000), category: 'security', action: 'PASSWORD_RESET', actor: 'michael.brown@email.com', actorType: 'user', target: 'User Account #U-3312', details: 'Password reset requested and completed via email verification. New session created.', severity: 'info', ipAddress: '156.78.90.12', sessionId: 'sess_p7q8r9', status: 'success', metadata: { method: 'email_verify' } },
  { id: 'LOG-011', timestamp: new Date(Date.now() - 5 * 3600000), category: 'booking', action: 'BOOKING_MODIFIED', actor: 'michael.brown@email.com', actorType: 'user', target: 'Booking CD9P2Q', details: 'Flight date changed from 10 Mar to 17 Mar 2026. Modification fee £45 charged. PNR updated.', severity: 'info', ipAddress: '156.78.90.12', sessionId: 'sess_p7q8r9', status: 'success', metadata: { pnr: 'CD9P2Q', fee: '£45' } },
  { id: 'LOG-012', timestamp: new Date(Date.now() - 6 * 3600000), category: 'system', action: 'SERVICE_RESTART', actor: 'system', actorType: 'system', target: 'Price Monitoring Service', details: 'Price monitoring service restarted after memory leak detected. 847 active monitors resumed.', severity: 'warning', ipAddress: '10.0.0.2', sessionId: 'sys_monitor', status: 'success', metadata: { monitors: '847', reason: 'memory_leak' } },
  { id: 'LOG-013', timestamp: new Date(Date.now() - 8 * 3600000), category: 'admin', action: 'COMMISSION_APPROVED', actor: 'finance@dialforflight.co.uk', actorType: 'admin', target: 'Partner #P-112', details: 'Monthly commission payout of £3,420 approved for TravelPro Agency. Bank transfer initiated.', severity: 'info', ipAddress: '192.168.1.20', sessionId: 'sess_s1t2u3', status: 'success', metadata: { amount: '£3,420', partner: 'TravelPro' } },
  { id: 'LOG-014', timestamp: new Date(Date.now() - 12 * 3600000), category: 'security', action: 'API_KEY_ROTATED', actor: 'admin@dialforflight.co.uk', actorType: 'admin', target: 'Anthropic API Key', details: 'API key rotated as part of monthly security protocol. Old key invalidated. New key deployed.', severity: 'info', ipAddress: '192.168.1.10', sessionId: 'sess_a1b2c3', status: 'success', metadata: { protocol: 'monthly_rotation' } },
  { id: 'LOG-015', timestamp: new Date(Date.now() - 24 * 3600000), category: 'system', action: 'INTEGRATION_ACTIVATED', actor: 'system', actorType: 'system', target: 'Expedia Affiliate', details: 'Expedia affiliate integration activated. Webhook endpoints configured. Test transaction successful.', severity: 'info', ipAddress: '10.0.0.1', sessionId: 'sys_integration', status: 'success', metadata: { partner: 'Expedia', test: 'passed' } },
];

function formatTimestamp(date: Date): string {
  return date.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export default function AuditLogsInteractive() {
  const [logs] = useState<AuditLog[]>(SAMPLE_LOGS);
  const [categoryFilter, setCategoryFilter] = useState<EventCategory | 'all'>('all');
  const [severityFilter, setSeverityFilter] = useState<Severity | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'success' | 'failed'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<'1h' | '24h' | '7d' | '30d'>('24h');

  const filteredLogs = useMemo(() => {
    const cutoff = {
      '1h': Date.now() - 3600000,
      '24h': Date.now() - 86400000,
      '7d': Date.now() - 7 * 86400000,
      '30d': Date.now() - 30 * 86400000,
    }[dateRange];

    return logs.filter(log => {
      if (log.timestamp.getTime() < cutoff) return false;
      if (categoryFilter !== 'all' && log.category !== categoryFilter) return false;
      if (severityFilter !== 'all' && log.severity !== severityFilter) return false;
      if (statusFilter !== 'all' && log.status !== statusFilter) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        return log.action.toLowerCase().includes(q) || log.actor.toLowerCase().includes(q) || log.details.toLowerCase().includes(q) || log.target.toLowerCase().includes(q);
      }
      return true;
    });
  }, [logs, categoryFilter, severityFilter, statusFilter, searchQuery, dateRange]);

  const stats = useMemo(() => ({
    total: filteredLogs.length,
    errors: filteredLogs.filter(l => l.severity === 'error' || l.severity === 'critical').length,
    failed: filteredLogs.filter(l => l.status === 'failed').length,
    security: filteredLogs.filter(l => l.category === 'security').length,
  }), [filteredLogs]);

  const exportLogs = () => {
    const headers = ['ID', 'Timestamp', 'Category', 'Action', 'Actor', 'Target', 'Details', 'Severity', 'Status', 'IP Address'];
    const rows = filteredLogs.map(l => [
      l.id, formatTimestamp(l.timestamp), l.category, l.action, l.actor, l.target,
      `"${l.details.replace(/"/g, '""')}"`, l.severity, l.status, l.ipAddress
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-gray-700 to-gray-900 rounded-xl flex items-center justify-center shadow-lg">
            <Icon name="ClipboardDocumentListIcon" size={24} className="text-white" />
          </div>
          <div>
            <h1 className="font-heading text-3xl md:text-4xl font-black text-foreground">Audit Logs</h1>
            <p className="text-muted-foreground text-sm">Track all admin actions, user events, and system changes</p>
          </div>
        </div>
        <button
          onClick={exportLogs}
          className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-primary to-secondary text-white text-sm font-bold rounded-lg shadow hover:shadow-lg transition-all hover:scale-105"
        >
          <Icon name="ArrowDownTrayIcon" size={16} />
          Export CSV
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Total Events', value: stats.total, icon: 'ClipboardDocumentListIcon', color: 'text-primary', bg: 'bg-primary/10' },
          { label: 'Errors', value: stats.errors, icon: 'ExclamationCircleIcon', color: 'text-red-600', bg: 'bg-red-50' },
          { label: 'Failed Actions', value: stats.failed, icon: 'XCircleIcon', color: 'text-orange-600', bg: 'bg-orange-50' },
          { label: 'Security Events', value: stats.security, icon: 'ShieldExclamationIcon', color: 'text-purple-600', bg: 'bg-purple-50' },
        ].map(stat => (
          <div key={stat.label} className="bg-white rounded-xl border border-border p-4 flex items-center gap-3 shadow-sm">
            <div className={`w-10 h-10 ${stat.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
              <Icon name={stat.icon as any} size={20} className={stat.color} />
            </div>
            <div>
              <p className={`text-2xl font-black ${stat.color}`}>{stat.value}</p>
              <p className="text-xs text-muted-foreground font-medium">{stat.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-border shadow-sm p-4 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="relative lg:col-span-2">
            <Icon name="MagnifyingGlassIcon" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search actions, actors, targets..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
            />
          </div>
          <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value as any)} className="px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 bg-white">
            <option value="all">All Categories</option>
            <option value="admin">Admin</option>
            <option value="user">User</option>
            <option value="booking">Booking</option>
            <option value="system">System</option>
            <option value="security">Security</option>
          </select>
          <select value={severityFilter} onChange={e => setSeverityFilter(e.target.value as any)} className="px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 bg-white">
            <option value="all">All Severities</option>
            <option value="info">Info</option>
            <option value="warning">Warning</option>
            <option value="error">Error</option>
            <option value="critical">Critical</option>
          </select>
          <select value={dateRange} onChange={e => setDateRange(e.target.value as any)} className="px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 bg-white">
            <option value="1h">Last 1 Hour</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>
        </div>
        <div className="flex gap-2 mt-3">
          {(['all', 'success', 'failed'] as const).map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                statusFilter === s ? 'bg-primary text-white' : 'bg-surface text-muted-foreground hover:text-foreground'
              }`}
            >
              {s === 'all' ? 'All Status' : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Log Table */}
      <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-surface/70 border-b border-border">
                <th className="text-left px-4 py-3 font-bold text-muted-foreground text-xs uppercase tracking-wider">Event</th>
                <th className="text-left px-4 py-3 font-bold text-muted-foreground text-xs uppercase tracking-wider hidden md:table-cell">Actor</th>
                <th className="text-left px-4 py-3 font-bold text-muted-foreground text-xs uppercase tracking-wider hidden lg:table-cell">Target</th>
                <th className="text-left px-4 py-3 font-bold text-muted-foreground text-xs uppercase tracking-wider">Severity</th>
                <th className="text-left px-4 py-3 font-bold text-muted-foreground text-xs uppercase tracking-wider hidden sm:table-cell">Status</th>
                <th className="text-left px-4 py-3 font-bold text-muted-foreground text-xs uppercase tracking-wider">Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredLogs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-16 text-center text-muted-foreground">
                    <Icon name="ClipboardDocumentListIcon" size={40} className="mx-auto mb-2 opacity-30" />
                    <p className="font-medium">No logs found</p>
                    <p className="text-xs mt-1">Try adjusting your filters</p>
                  </td>
                </tr>
              ) : (
                filteredLogs.map(log => {
                  const catConf = CATEGORY_CONFIG[log.category];
                  const sevConf = SEVERITY_CONFIG[log.severity];
                  const isExpanded = expandedId === log.id;
                  return (
                    <>
                      <tr
                        key={log.id}
                        className={`hover:bg-surface/50 cursor-pointer transition-colors ${
                          log.severity === 'critical' ? 'bg-red-50/30' : log.severity === 'error' ? 'bg-orange-50/20' : ''
                        }`}
                        onClick={() => setExpandedId(isExpanded ? null : log.id)}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className={`w-7 h-7 ${catConf.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                              <Icon name={catConf.icon as any} size={14} className={catConf.color} />
                            </div>
                            <div>
                              <p className="font-bold text-foreground text-xs">{log.action.replace(/_/g, ' ')}</p>
                              <p className="text-xs text-muted-foreground">{log.id}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <div className="flex items-center gap-1.5">
                            <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                              log.actorType === 'admin' ? 'bg-purple-100' : log.actorType === 'system' ? 'bg-gray-100' : 'bg-blue-100'
                            }`}>
                              <Icon name={log.actorType === 'admin' ? 'ShieldCheckIcon' : log.actorType === 'system' ? 'CpuChipIcon' : 'UserIcon'} size={10} className={log.actorType === 'admin' ? 'text-purple-600' : log.actorType === 'system' ? 'text-gray-600' : 'text-blue-600'} />
                            </div>
                            <span className="text-xs text-muted-foreground truncate max-w-[140px]">{log.actor}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          <span className="text-xs text-muted-foreground">{log.target}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${sevConf.bg} ${sevConf.color}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${sevConf.dot}`}></span>
                            {sevConf.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${
                            log.status === 'success' ? 'bg-green-50 text-green-700' : log.status === 'failed' ? 'bg-red-50 text-red-700' : 'bg-yellow-50 text-yellow-700'
                          }`}>
                            <Icon name={log.status === 'success' ? 'CheckCircleIcon' : log.status === 'failed' ? 'XCircleIcon' : 'ClockIcon'} size={10} />
                            {log.status}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-muted-foreground whitespace-nowrap">
                              {log.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                            <Icon name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={12} className="text-muted-foreground" />
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr key={`${log.id}-expanded`} className="bg-surface/30">
                          <td colSpan={6} className="px-4 py-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-1">Details</p>
                                <p className="text-sm text-foreground">{log.details}</p>
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-1">IP Address</p>
                                  <p className="text-sm font-mono text-foreground">{log.ipAddress}</p>
                                </div>
                                <div>
                                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-1">Session</p>
                                  <p className="text-sm font-mono text-foreground">{log.sessionId}</p>
                                </div>
                                <div>
                                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-1">Timestamp</p>
                                  <p className="text-xs text-foreground">{formatTimestamp(log.timestamp)}</p>
                                </div>
                                {log.metadata && (
                                  <div>
                                    <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-1">Metadata</p>
                                    <div className="flex flex-wrap gap-1">
                                      {Object.entries(log.metadata).map(([k, v]) => (
                                        <span key={k} className="text-xs bg-white border border-border px-1.5 py-0.5 rounded font-mono">{k}: {v}</span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-border bg-surface/30 flex items-center justify-between">
          <p className="text-xs text-muted-foreground">Showing {filteredLogs.length} of {logs.length} log entries</p>
          <button onClick={exportLogs} className="flex items-center gap-1.5 text-xs font-bold text-primary hover:text-secondary transition-colors">
            <Icon name="ArrowDownTrayIcon" size={14} />
            Export filtered logs
          </button>
        </div>
      </div>
    </div>
  );
}
